import { Component, OnInit, ChangeDetectionStrategy, inject, input, effect } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { CreateCampaignLogicService } from '../../services/create-campaign-logic.service';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { FacebookHelperService } from '../../services/facebook-helper.service';
import { distinctUntilChanged, filter, map, Observable, of, startWith, take, tap } from 'rxjs';
import { ApplicationEnum } from 'src/app/models';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';

@Component({
    selector: 'likes-for-post',
    templateUrl: './likes-for-post.component.html',
    styleUrls: ['./likes-for-post.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [AsyncPipe, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatDatepickerModule, MatButtonModule],
    providers: [provideNativeDateAdapter()]
})
export class LikesForPostComponent implements OnInit {

  // Signal-based inputs
  applicationId = input<string | undefined>(undefined);
  ownerUnitId = input<string | undefined>(undefined);
  regionId = input<string | undefined>(undefined);
  gender = input<string | undefined>(undefined);
  ageFrom = input<number | undefined>(undefined);
  ageTo = input<number | undefined>(undefined);

  private appId$ = toObservable(this.applicationId);
  private ownerUnitId$ = toObservable(this.ownerUnitId);
  private regionId$ = toObservable(this.regionId);
  private gender$ = toObservable(this.gender);
  private ageFrom$ = toObservable(this.ageFrom);
  private ageTo$ = toObservable(this.ageTo);

  constructor() {
    // when the parent passes a new value to any of these inputs,
    // Angular re-executes this block and triggers form revalidation.
    effect(() => {
      this.applicationId();
      this.ownerUnitId();
      this.regionId();
      this.gender();
      this.ageFrom();
      this.ageTo();
      this.engagementFormGroup?.updateValueAndValidity();
    });
  }

  private readonly TIKTOK_VIDEO_URL_REGEX = /tiktok\.com\/@[^/]+\/video\/\d+|vm\.tiktok\.com\//;

  public engagementFormGroup: FormGroup = new FormGroup({});
  public countAvatarsInGroup$!: Observable<number | null>;
  public getGroupRegions$!: Observable<string[] | null>

  private createCampaignService = inject(CreateCampaignLogicService);
  private facebookHelperService = inject(FacebookHelperService);
  private fb = inject(FormBuilder);
  public dialog = inject(MatDialog);

  get isFacebook(): boolean { return this.applicationId() === ApplicationEnum.facebook; }


  ngOnInit(): void { 

    const now = new Date();

    // Format time as HH:mm (e.g., "14:30")
    const currentTime = now.getHours().toString().padStart(2, '0') + ':' + 
                        now.getMinutes().toString().padStart(2, '0');
   
    const isTikTok = this.applicationId() === ApplicationEnum.tiktok;
    const urlRegex = isTikTok ? this.TIKTOK_VIDEO_URL_REGEX : this.facebookHelperService.COMBINED_FB_POST_REGEX;
    const asyncValidators = isTikTok
      ? [this.avatarsLimitValidator()]
      : [this.avatarsLimitValidator(), this.groupRegionValidator()];

    this.engagementFormGroup = this.fb.group(
      {
        postUrl: [null, [Validators.required, Validators.pattern(urlRegex)]],
        resourceType: [isTikTok ? 'page' : null],
        groupId: [null],
        numberOfLikes: new FormControl('', [
          Validators.required,
          Validators.min(0),
          Validators.pattern("^[0-9]*$")
        ]),
        secondsBetweenLikes: new FormControl(30, [
          Validators.required,
          Validators.min(0),
          Validators.pattern("^[0-9]*$")
        ]),
        startDate:  [now, Validators.required],
        startTime: [currentTime, Validators.required]
      },
      {
         asyncValidators: asyncValidators
      }
    );

    // Create a stream for groupId changes
    const groupId$ = this.engagementFormGroup.get('groupId')!.valueChanges.pipe(
      startWith(this.engagementFormGroup.value),
      map(() => this.engagementFormGroup.getRawValue().groupId),
      distinctUntilChanged(), // Only trigger if groupId actually changes
      tap(groupId => {
        console.log(`new groupId is: ${groupId}`)
      })      
    );

    const resourceType$ = this.engagementFormGroup.get('resourceType')!.valueChanges.pipe(
      startWith(this.engagementFormGroup.get('resourceType')!.value),
      distinctUntilChanged()
    );

    // this code retrieves avatars count from DB each time a control or input changes
    // it is used for displaying the avatars limit message
    this.countAvatarsInGroup$ = this.createCampaignService.getAvatarCount$(
      this.appId$,
      this.ownerUnitId$,
      groupId$,
      resourceType$,
      this.regionId$,
      this.gender$,
      this.ageFrom$,
      this.ageTo$
    );

    this.getGroupRegions$ = this.createCampaignService.getGroupRegions$(groupId$)

  }


  ngAfterViewInit() {
    // Log form status changes
    this.engagementFormGroup.statusChanges.subscribe(status => {
      // console.log('Form status:', status);
      // console.log('Form errors:', this.engagementFormGroup.errors);
      // console.log('Form valid:', this.engagementFormGroup.valid);
    });

    // Log individual control status
    Object.keys(this.engagementFormGroup.controls).forEach(key => {
      const control = this.engagementFormGroup.get(key);
      control?.statusChanges.subscribe(status => {
        console.log(`${key} status:`, status, 'errors:', control.errors);
      });
    });
  }

  // Prevents typing non-numeric characters (including 'e', '.', '-', '+')
  onlyNumbers(event: KeyboardEvent) {
    const pattern = /[0-9]/;
    const inputChar = String.fromCharCode(event.charCode);
    if (!pattern.test(inputChar)) {
      event.preventDefault();
    }
  }


  onPostUrlBlur(): void {
    const control = this.engagementFormGroup.get('postUrl');

    if (control?.invalid || !control?.value) {
      this.engagementFormGroup.patchValue({ resourceType: null, groupId: null });
      return;
    }

    if (this.applicationId() === ApplicationEnum.tiktok) {
      this.engagementFormGroup.patchValue({ resourceType: 'page', groupId: null });
      return;
    }

    const result = this.facebookHelperService.parseUrl(control.value);
    this.engagementFormGroup.patchValue(result, { emitEvent: true });
  }

  avatarsLimitValidator(): AsyncValidatorFn {
      return (control: AbstractControl): Observable<ValidationErrors | null> => {
        const numberOfLikes = control.get('numberOfLikes')?.value || 0;
  
        if (numberOfLikes === 0) return of(null);
  
        return this.countAvatarsInGroup$.pipe(
          // filter out 'null' so the validator waits for the real server response
          filter(val => val !== null),
          take(1),
          map(availableCount => {
            if (availableCount === -1) {
              return { avatarCountError: true };
            }
            if (availableCount !== null && availableCount < numberOfLikes) {
              return { insufficientAvatars: { available: availableCount, required: numberOfLikes } };
            }
            return null;
          })
        );
      };
  }

  groupRegionValidator():AsyncValidatorFn {
     return (control: AbstractControl): Observable<ValidationErrors | null> => {
        const resourceType = control.get('resourceType')?.value;
        const groupId = control.get('groupId')?.value;
        const region_id = this.regionId();
  
        if (resourceType == 'page' || !region_id) return of(null);
  
        return this.getGroupRegions$.pipe(
          take(1),
          map(region_list => {
            if (region_list && region_list.includes(region_id)) {
              return null;
            }
            return {groupRegionMismatch: {groupId, region_id}}
          })
        )
      };
  }

}
